/* ==========================================================================
   ADORN COLLECTIVE — behaviour layer
   Vanilla ES2022. No framework, no jQuery, no build step.

   This file owns ONLY the shared chrome behaviours that more than one section
   needs. Product, cart and search logic belong to their own sections.

   Everything is wired through data- attributes so a section can opt in from
   Liquid without touching this file:

     [data-nav-toggle]           button that opens/closes the mobile nav
     [data-nav-panel]            the panel it controls
     [data-disclosure]           wrapper for a toggle + panel pair
     [data-disclosure-toggle]    button; gets aria-expanded managed
     [data-disclosure-panel]     the region it shows/hides
     [data-cart-count]           element whose text is the item count
     [data-cart-count-wrapper]   optional wrapper, hidden when count is 0

   Public surface (window.Adorn):
     on(type, selector, handler, root?)  delegated listener; returns unbind fn
     setCartCount(n)                     write the count everywhere
     refreshCart()                       fetch /cart.js and broadcast
     closeDisclosures(except?)           collapse open disclosures
     closeNav()                          close the mobile nav

   Events:
     'cart:updated'  CustomEvent on document. detail may be a full Shopify
                     cart object, { item_count: n }, or nothing (triggers a
                     fetch). Any section that mutates the cart should dispatch
                     it rather than reaching into the header.
   ========================================================================== */

(() => {
  'use strict';

  /* ----------------------------------------------------------------------
     Delegation helper
     One listener at the root instead of one per node, so markup rendered
     later (Section Rendering API, cart drawer re-render) is live with no
     re-binding step.
     -------------------------------------------------------------------- */

  const on = (type, selector, handler, root = document) => {
    const listener = (event) => {
      const target = event.target instanceof Element
        ? event.target.closest(selector)
        : null;
      if (target && root.contains(target)) handler(event, target);
    };
    root.addEventListener(type, listener, type === 'focusin' || type === 'focusout');
    return () => root.removeEventListener(type, listener);
  };

  const $  = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----------------------------------------------------------------------
     Mobile navigation
     The toggle owns aria-expanded; the panel owns [hidden]. Focus moves into
     the panel on open and returns to the toggle on close, which is the part
     most themes skip and the part keyboard users actually feel.
     -------------------------------------------------------------------- */

  const nav = {
    toggle: null,
    panel: null,
    lastFocused: null,

    get isOpen() {
      return this.toggle?.getAttribute('aria-expanded') === 'true';
    },

    resolve() {
      this.toggle = $('[data-nav-toggle]');
      this.panel = this.toggle
        ? (document.getElementById(this.toggle.getAttribute('aria-controls') || '') || $('[data-nav-panel]'))
        : $('[data-nav-panel]');
      return Boolean(this.toggle && this.panel);
    },

    open() {
      if (!this.resolve() || this.isOpen) return;
      this.lastFocused = document.activeElement;
      this.toggle.setAttribute('aria-expanded', 'true');
      this.panel.hidden = false;
      document.documentElement.classList.add('nav-is-open');
      document.body.classList.add('no-scroll');

      const first = $(FOCUSABLE, this.panel);
      (first || this.panel).focus({ preventScroll: true });

      document.dispatchEvent(new CustomEvent('nav:opened'));
    },

    close({ restoreFocus = true } = {}) {
      if (!this.resolve() || !this.isOpen) return;
      this.toggle.setAttribute('aria-expanded', 'false');
      this.panel.hidden = true;
      document.documentElement.classList.remove('nav-is-open');
      document.body.classList.remove('no-scroll');

      if (restoreFocus) {
        const target = this.lastFocused instanceof HTMLElement ? this.lastFocused : this.toggle;
        target.focus({ preventScroll: true });
      }
      this.lastFocused = null;

      document.dispatchEvent(new CustomEvent('nav:closed'));
    },

    // Keep Tab inside the panel while it owns the viewport.
    trap(event) {
      if (!this.isOpen || event.key !== 'Tab') return;
      const items = $$(FOCUSABLE, this.panel).filter((el) => el.offsetParent !== null);
      if (!items.length) return;

      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  on('click', '[data-nav-toggle]', (event) => {
    event.preventDefault();
    nav.isOpen ? nav.close() : nav.open();
  });

  on('click', '[data-nav-close]', (event) => {
    event.preventDefault();
    nav.close();
  });

  /* ----------------------------------------------------------------------
     Disclosures
     Covers dropdowns, mobile submenus, accordions, filter groups and FAQ
     rows — anything that is "a button that reveals a region". Nested
     disclosures are supported; only true siblings close each other.
     -------------------------------------------------------------------- */

  // Hover-disclosures that hover (not a click) opened. A mouse user who hovers a
  // department then clicks its trigger means "open", not "toggle shut": the first
  // click after a hover-open confirms the panel instead of closing it.
  const openedByHover = new WeakSet();

  const disclosure = {
    panelFor(toggle) {
      const id = toggle.getAttribute('aria-controls');
      if (id) {
        const byId = document.getElementById(id);
        if (byId) return byId;
      }
      const wrapper = toggle.closest('[data-disclosure]');
      return wrapper ? $('[data-disclosure-panel]', wrapper) : null;
    },

    isOpen(toggle) {
      return toggle.getAttribute('aria-expanded') === 'true';
    },

    set(toggle, open) {
      const panel = this.panelFor(toggle);
      toggle.setAttribute('aria-expanded', String(open));
      if (panel) panel.hidden = !open;
      toggle.closest('[data-disclosure]')?.classList.toggle('is-open', open);
      if (!open) {
        const hoverWrapper = toggle.closest('[data-disclosure-hover]');
        if (hoverWrapper) openedByHover.delete(hoverWrapper);
      }
    },

    open(toggle) {
      // Close siblings so a menu bar never shows two open dropdowns at once.
      const group = toggle.closest('[data-disclosure-group]');
      if (group) {
        $$('[data-disclosure-toggle]', group)
          .filter((other) => other !== toggle && !toggle.contains(other) && !other.contains(toggle))
          .forEach((other) => this.set(other, false));
      }
      this.set(toggle, true);
    },

    close(toggle) { this.set(toggle, false); },

    closeAll(except = null) {
      $$('[data-disclosure-toggle][aria-expanded="true"]')
        .filter((toggle) => toggle !== except && !toggle.contains(except))
        .forEach((toggle) => this.set(toggle, false));
    }
  };

  on('click', '[data-disclosure-toggle]', (event, toggle) => {
    event.preventDefault();
    const hoverWrapper = toggle.closest('[data-disclosure-hover]');
    if (hoverWrapper && openedByHover.has(hoverWrapper) && disclosure.isOpen(toggle)) {
      openedByHover.delete(hoverWrapper);
      return;
    }
    disclosure.isOpen(toggle) ? disclosure.close(toggle) : disclosure.open(toggle);
  });

  // Hover-intent for pointer devices that actually hover. Touch is excluded
  // so a tap never both opens and immediately follows the link.
  //
  // pointerenter/pointerleave are the non-bubbling counterparts of
  // pointerover/pointerout. on() delegates from a single root listener and
  // relies on bubbling to find the target via closest() — a non-bubbling
  // type can never reach a document-level listener, so the previous
  // pointerenter/pointerleave delegation here was dead code; hovering never
  // opened anything. pointerover/pointerout DO bubble, so delegation works,
  // but they also fire on every move between descendants inside the same
  // wrapper (e.g. sliding from the toggle onto the open panel). The
  // relatedTarget check reproduces true enter/leave semantics — fire only
  // when the pointer actually crosses the wrapper's boundary — the same
  // technique browsers use internally to synthesize mouseenter/mouseleave
  // from mouseover/mouseout.
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  const crossedBoundary = (event, wrapper) => {
    const related = event.relatedTarget;
    return !(related instanceof Node && wrapper.contains(related));
  };

  // Leaving closes after a grace period, not instantly: a diagonal path from
  // the trigger toward a far column clips outside the wrapper for a few
  // frames, and an instant close drops the panel out from under the cursor.
  // Re-entering within the grace period cancels the close.
  const HOVER_CLOSE_DELAY_MS = 250;
  const hoverCloseTimers = new WeakMap();

  const cancelHoverClose = (wrapper) => {
    clearTimeout(hoverCloseTimers.get(wrapper));
    hoverCloseTimers.delete(wrapper);
  };

  on('pointerover', '[data-disclosure-hover]', (event, wrapper) => {
    if (!canHover.matches || !crossedBoundary(event, wrapper)) return;
    cancelHoverClose(wrapper);
    const toggle = $('[data-disclosure-toggle]', wrapper);
    if (toggle && !disclosure.isOpen(toggle)) {
      disclosure.open(toggle);
      openedByHover.add(wrapper);
    }
  });

  on('pointerout', '[data-disclosure-hover]', (event, wrapper) => {
    if (!canHover.matches || !crossedBoundary(event, wrapper)) return;
    const toggle = $('[data-disclosure-toggle]', wrapper);
    if (!toggle) return;
    cancelHoverClose(wrapper);
    hoverCloseTimers.set(wrapper, setTimeout(() => {
      hoverCloseTimers.delete(wrapper);
      if (!wrapper.matches(':hover')) disclosure.close(toggle);
    }, HOVER_CLOSE_DELAY_MS));
  });

  // Click outside collapses any open disclosure. Guarded so a click that
  // lands on a toggle (or anything else inside an open disclosure) never
  // races its own open()/close() call in the same event pass — the toggle
  // is itself inside [data-disclosure], so `inside` is truthy and this
  // never fires for the click that just opened/closed it.
  document.addEventListener('click', (event) => {
    const inside = event.target instanceof Element
      ? event.target.closest('[data-disclosure]')
      : null;
    if (!inside) disclosure.closeAll();
  });

  // Escape closes the innermost thing that is open.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    if (nav.isOpen) {
      nav.close();
      return;
    }
    const open = $$('[data-disclosure-toggle][aria-expanded="true"]');
    if (open.length) {
      const last = open[open.length - 1];
      disclosure.close(last);
      last.focus({ preventScroll: true });
    }
  });

  document.addEventListener('keydown', (event) => nav.trap(event));

  // Leaving a disclosure by keyboard should collapse it too.
  on('focusout', '[data-disclosure]', (event, wrapper) => {
    // Hover-disclosures are a desktop behaviour. In the mobile drawer the same nav items are
    // tap accordions: closing one on focusout collapsed the list mid-tap, the release landed
    // outside every disclosure, and closeAll() shut the whole menu.
    if (!window.matchMedia('(min-width: 900px)').matches) return;
    const next = event.relatedTarget;
    if (next instanceof Node && wrapper.contains(next)) return;
    const toggle = $('[data-disclosure-toggle]', wrapper);
    if (toggle && disclosure.isOpen(toggle) && wrapper.hasAttribute('data-disclosure-hover')) {
      disclosure.close(toggle);
    }
  });

  /* ----------------------------------------------------------------------
     Cart count
     The header renders a server-side count on load. Anything that mutates
     the cart dispatches 'cart:updated' and this keeps every badge in sync —
     the header never has to know who changed the cart.
     -------------------------------------------------------------------- */

  let cartRequest = null;

  const setCartCount = (count) => {
    const value = Number.isFinite(Number(count)) ? Math.max(0, Number(count)) : 0;

    $$('[data-cart-count]').forEach((node) => {
      node.textContent = String(value);
      node.setAttribute('data-count', String(value));
    });

    $$('[data-cart-count-wrapper]').forEach((node) => {
      node.hidden = value === 0;
    });

    // The bag link's name when the badge is hidden (count 0): plain "Bag".
    $$('[data-cart-empty-label]').forEach((node) => {
      node.hidden = value !== 0;
    });

    // Let the accessible name carry the number too, not just the pill.
    $$('[data-cart-label]').forEach((node) => {
      const template = node.getAttribute('data-cart-label');
      if (template) node.setAttribute('aria-label', template.replace('{{ count }}', String(value)));
    });

    // The screen-reader text inside the badge ("Bag, 2 items"): rendered for 1 and 2 on the
    // server in the shop's language, the number swapped in here.
    $$('[data-cart-count-label]').forEach((node) => {
      node.textContent = value === 1
        ? node.dataset.labelOne
        : (node.dataset.labelOther || '').replace(/\b2\b/, String(value));
    });

    document.documentElement.style.setProperty('--cart-count', String(value));
    return value;
  };

  const readCount = (detail) => {
    if (detail == null) return null;
    if (typeof detail === 'number') return detail;
    if (typeof detail.item_count === 'number') return detail.item_count;
    if (typeof detail.itemCount === 'number') return detail.itemCount;
    if (detail.cart && typeof detail.cart.item_count === 'number') return detail.cart.item_count;
    return null;
  };

  const refreshCart = async () => {
    if (cartRequest) return cartRequest;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    cartRequest = fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (cart) setCartCount(cart.item_count);
        return cart;
      })
      .catch(() => null)
      .finally(() => { cartRequest = null; });

    return cartRequest;
  };

  document.addEventListener('cart:updated', (event) => {
    const count = readCount(event.detail);
    if (count === null) refreshCart();
    else setCartCount(count);
  });

  // Coming back via bfcache can restore a stale count.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshCart();
  });

  // A page prerendered by the speculation rules (layout/theme.liquid) was rendered before the
  // shopper's latest add to bag; re-read the count the moment it is actually shown.
  if (document.prerendering) {
    document.addEventListener('prerenderingchange', () => refreshCart(), { once: true });
  }

  /* ----------------------------------------------------------------------
     Add-to-bag confirmation + quick add
     Any add (a product card's [data-quick-add] form, or the product page
     form) dispatches 'cart:updated' with { item }; the toast in
     layout/theme.liquid confirms it and links to the bag, so the shopper
     stays where they are.
     -------------------------------------------------------------------- */

  const toast = $('[data-cart-toast]');
  let toastTimer = null;

  const showToast = (message, isError = false) => {
    if (!toast) return;
    const text = $('[data-cart-toast-text]', toast);
    const link = $('[data-cart-toast-link]', toast);
    const checkoutBtn = $('[data-cart-toast-checkout]', toast);
    text.textContent = '';
    if (isError) {
      text.textContent = message || toast.dataset.error;
    } else {
      const heading = document.createElement('strong');
      heading.textContent = (toast.dataset.added || 'Added to bag') + ': ';
      text.append(heading, document.createTextNode(message || ''));
    }
    if (link) link.hidden = isError;
    if (checkoutBtn) checkoutBtn.hidden = isError;
    toast.classList.toggle('cart-toast--error', isError);
    toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { toast.hidden = true; }, 6000);
  };

  const toastClose = toast && $('[data-cart-toast-close]', toast);
  if (toastClose) {
    toastClose.addEventListener('click', () => {
      toast.hidden = true;
      clearTimeout(toastTimer);
    });
  }

  document.addEventListener('cart:updated', (event) => {
    const item = event.detail && event.detail.item;
    if (item) showToast(item.product_title || item.title || '');
  });

  document.addEventListener('cart:error', (event) => {
    const message = event.detail && event.detail.message;
    showToast(message, true);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target instanceof HTMLFormElement && event.target.matches('[data-quick-add]') ? event.target : null;
    if (!form || !window.fetch) return; // no fetch: the native POST to /cart/add still works
    event.preventDefault();

    const button = $('button[type="submit"]', form);
    const label = button && $('[aria-hidden="true"]', button);
    if (!button || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    if (label) label.textContent = button.dataset.labelAdding;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      body: new FormData(form)
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        button.removeAttribute('aria-busy');
        if (!ok) {
          if (label) label.textContent = button.dataset.labelAdd;
          showToast(data && data.description, true);
          return;
        }
        if (label) label.textContent = button.dataset.labelAdded;
        setTimeout(() => { if (label) label.textContent = button.dataset.labelAdd; }, 2000);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item: data } }));
      })
      .catch(() => {
        button.removeAttribute('aria-busy');
        if (label) label.textContent = button.dataset.labelAdd;
        showToast(null, true);
      });
  });

  /* ----------------------------------------------------------------------
     Live product order
     A list marked [data-live-products="<collection url>?sort_by=…&section_id=bestsellers-grid"]
     is server-rendered in the collection's default order (a working no-JS
     fallback) and then replaced with the same collection in the requested
     order through the Section Rendering API — best-selling, newest, etc.
     [data-live-products-count] caps how many cards stay. A failed fetch
     leaves the server-rendered cards in place.
     -------------------------------------------------------------------- */

  // The product URL a card links to, the key used to keep already-painted cards.
  const cardKey = (li) => {
    const link = li.querySelector('a[href*="/products/"]');
    return link ? link.getAttribute('href').split('?')[0] : null;
  };

  const loadLiveProducts = (list) => {
    if (list.dataset.liveBound === '1') return;
    list.dataset.liveBound = '1';
    // Server order already is the requested order (the collection's own default sort): nothing
    // to fetch, and the eager first row stays the page's LCP image.
    const wanted = (list.dataset.liveProducts.match(/[?&]sort_by=([^&]+)/) || [])[1];
    if (wanted && wanted === list.dataset.defaultSort) return;
    fetch(list.dataset.liveProducts, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((html) => {
        const items = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-bestsellers-items]');
        if (!items || !items.children.length) return;
        const count = Number(list.dataset.liveProductsCount) || items.children.length;
        // Re-use a card that is already on screen instead of swapping in a copy, so its image is
        // neither aborted nor downloaded a second time.
        const current = new Map();
        Array.from(list.children).forEach((li) => {
          const key = cardKey(li);
          if (key && !current.has(key)) current.set(key, li);
        });
        const next = Array.from(items.children).slice(0, count).map((li) => {
          const key = cardKey(li);
          return (key && current.get(key)) || li;
        });
        const unchanged = next.length === list.children.length
          && next.every((li, index) => li === list.children[index]);
        if (!unchanged) list.replaceChildren(...next);
      })
      .catch(() => {});
  };

  // Only the visible panel is ordered at load; a hidden tab's panel is fetched the first time it
  // is shown (sections/bestsellers-tabs.liquid dispatches 'bestsellers:shown').
  const loadVisibleLiveProducts = (root = document) => {
    $$('[data-live-products]', root)
      .filter((list) => !list.closest('[hidden]'))
      .forEach(loadLiveProducts);
  };

  loadVisibleLiveProducts();
  document.addEventListener('bestsellers:shown', (event) => {
    if (event.target instanceof Element) $$('[data-live-products]', event.target).forEach(loadLiveProducts);
  });
  document.addEventListener('shopify:section:load', (event) => loadVisibleLiveProducts(event.target));

  /* ----------------------------------------------------------------------
     Predictive search
     The header search form still submits to /search on its own; this only
     adds up to six product and collection suggestions from Shopify's
     /search/suggest.json under the field as the shopper types.
       [data-predictive-search]          wrapper around the form
       [data-predictive-search-input]    the text field
       [data-predictive-search-results]  the list region it fills
       [data-predictive-search-status]   polite live region ("4 suggestions")
     -------------------------------------------------------------------- */

  const escapeHtml = (text) => String(text == null ? '' : text)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  const formatPrice = (amount) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    const value = Number(amount);
    if (!Number.isFinite(value)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(value);
    } catch (error) {
      return value.toFixed(2);
    }
  };

  const predictive = (root) => {
    if (root.dataset.predictiveBound === '1') return;
    root.dataset.predictiveBound = '1';
    const input = $('[data-predictive-search-input]', root);
    const results = $('[data-predictive-search-results]', root);
    const status = $('[data-predictive-search-status]', root);
    if (!input || !results || !window.fetch) return;

    const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    let timer = null;
    let controller = null;
    let lastTerm = '';

    const clear = () => {
      results.hidden = true;
      results.replaceChildren();
      if (status) status.textContent = '';
    };

    const links = () => $$('a', results);

    const render = (term, data) => {
      const found = (data && data.resources && data.resources.results) || {};
      const products = (found.products || []).slice(0, 6);
      const collections = (found.collections || []).slice(0, 3);
      const searchUrl = `${base}search?q=${encodeURIComponent(term)}&type=product`;
      let html = '';
      if (products.length) {
        html += `<p class="predictive__label">${escapeHtml(root.dataset.labelProducts)}</p><ul class="predictive__list" role="list">`;
        products.forEach((product) => {
          const image = product.featured_image && product.featured_image.url
            ? `<img src="${escapeHtml(product.featured_image.url)}${product.featured_image.url.includes('?') ? '&' : '?'}width=96" alt="" width="48" height="48" loading="lazy" class="predictive__image">`
            : '<span class="predictive__image" aria-hidden="true"></span>';
          html += `<li><a class="predictive__item" href="${escapeHtml(product.url)}">${image}<span class="predictive__text"><span class="predictive__title">${escapeHtml(product.title)}</span><span class="predictive__meta">${escapeHtml(product.vendor || '')}${product.price ? ` · ${escapeHtml(formatPrice(product.price))}` : ''}</span></span></a></li>`;
        });
        html += '</ul>';
      }
      if (collections.length) {
        html += `<p class="predictive__label">${escapeHtml(root.dataset.labelCollections)}</p><ul class="predictive__list" role="list">`;
        collections.forEach((collection) => {
          html += `<li><a class="predictive__item predictive__item--text" href="${escapeHtml(collection.url)}"><span class="predictive__title">${escapeHtml(collection.title)}</span></a></li>`;
        });
        html += '</ul>';
      }
      html += `<a class="predictive__all" href="${escapeHtml(searchUrl)}">${escapeHtml((root.dataset.labelAll || '').replace('__TERMS__', () => term))}</a>`;
      results.innerHTML = html;
      results.hidden = false;
      const total = products.length + collections.length;
      if (status) {
        status.textContent = total === 1
          ? root.dataset.statusOne
          : (root.dataset.statusOther || '').replace('__COUNT__', String(total));
      }
    };

    const query = (term) => {
      if (controller) controller.abort();
      controller = typeof AbortController === 'function' ? new AbortController() : null;
      const url = `${base}search/suggest.json?q=${encodeURIComponent(term)}`
        + '&resources[type]=product,collection&resources[limit]=6'
        + '&resources[options][unavailable_products]=last';
      fetch(url, { headers: { Accept: 'application/json' }, signal: controller ? controller.signal : undefined })
        .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
        .then((data) => { if (term === lastTerm) render(term, data); })
        .catch(() => {});
    };

    input.addEventListener('input', () => {
      const term = input.value.trim();
      lastTerm = term;
      clearTimeout(timer);
      if (term.length < 2) { clear(); return; }
      timer = setTimeout(() => query(term), 200);
    });

    // Arrow keys walk the field and the suggestion links; Escape returns to the field.
    root.addEventListener('keydown', (event) => {
      if (results.hidden) return;
      const items = links();
      const index = items.indexOf(document.activeElement);
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        (items[index + 1] || items[0])?.focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (index <= 0) input.focus();
        else items[index - 1].focus();
      } else if (event.key === 'Escape' && index !== -1) {
        event.stopPropagation();
        input.focus();
      }
    });
  };

  $$('[data-predictive-search]').forEach(predictive);

  /* ----------------------------------------------------------------------
     Cross-document view transitions
     @view-transition (layout/theme.liquid) morphs the product-card image
     into the product page's main image. Exactly one element per page may
     carry a given view-transition-name, so the name is set only on the
     card image that was clicked, never on the whole grid.
     -------------------------------------------------------------------- */

  const VT_NAME = 'adorn-product-image';

  on('click', '.product-card__link', (event, link) => {
    // E-commerce Telemetry: select_item tracking
    try {
      const itemId = link.dataset.itemId;
      if (itemId) {
        const itemPayload = {
          item_id: itemId,
          item_name: link.dataset.itemTitle || '',
          price: Number(link.dataset.itemPrice) || 0,
          item_brand: link.dataset.itemBrand || ''
        };
        document.dispatchEvent(new CustomEvent('analytics:select_item', { detail: { item: itemPayload } }));
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'select_item', {
            item_list_name: document.title || 'Product List',
            items: [itemPayload]
          });
        }
      }
    } catch (telemetryErr) {}

    if (prefersReducedMotion() || event.defaultPrevented || event.button !== 0
      || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    $$('[data-vt-source]').forEach((node) => { node.style.viewTransitionName = ''; node.removeAttribute('data-vt-source'); });
    const image = $('.product-card__image', link);
    if (!image) return;
    // A product page links to others (related products): its own main image gives up the name.
    document.documentElement.setAttribute('data-vt-card', '');
    image.style.viewTransitionName = VT_NAME;
    image.setAttribute('data-vt-source', '');
  });

  // Back/forward cache restores the page as it was left: drop the name so the next click starts clean.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.documentElement.removeAttribute('data-vt-card');
    $$('[data-vt-source]').forEach((node) => { node.style.viewTransitionName = ''; node.removeAttribute('data-vt-source'); });
  });

  /* ----------------------------------------------------------------------
     Housekeeping
     -------------------------------------------------------------------- */

  // Nav is a mobile affordance; a resize past the breakpoint must not leave
  // the body locked with an invisible panel open.
  const desktop = window.matchMedia('(min-width: 900px)');
  const onBreakpoint = (event) => { if (event.matches) nav.close({ restoreFocus: false }); };
  desktop.addEventListener('change', onBreakpoint);

  // Theme editor re-renders a section's DOM wholesale; re-sync afterwards.
  document.addEventListener('shopify:section:load', () => {
    refreshCart();
  });

  document.addEventListener('shopify:section:select', () => {
    disclosure.closeAll();
  });

  // Mark the document so CSS can react to JS being available without
  // flashing un-enhanced markup on a slow connection.
  document.documentElement.classList.add('js');
  document.documentElement.classList.remove('no-js');
  if (prefersReducedMotion()) document.documentElement.classList.add('reduced-motion');

  /* ----------------------------------------------------------------------
     Public surface
     -------------------------------------------------------------------- */

  window.Adorn = Object.freeze({
    on,
    setCartCount,
    refreshCart,
    closeDisclosures: (except) => disclosure.closeAll(except),
    closeNav: () => nav.close(),
    openNav: () => nav.open()
  });
})();
